/* @flow */

import type { Mode } from "state/view-mode";

import React, { useRef, useEffect, useLayoutEffect, useState, useContext } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import cn from "classnames";
import { useTranslate } from "@awardit/react-use-translate";
import useSearchSuggestions from "helpers/use-search-suggestions";
import { AnalyticsContext } from "@crossroads/analytics";
import SearchIcon from "icons/search.svg";
import CloseIcon from "icons/close-small.svg";
import { MODE } from "state/view-mode";

import styles from "./styles.scss";

type Props = {
  className?: string,
  autoFocus?: boolean,
  setMode?: Mode => void,
};

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const SearchMenu = ({
  className = "",
  autoFocus = false,
  setMode,
}: Props): React$Node => {
  const history = useHistory();
  const location = useLocation();
  const t = useTranslate();
  const inputRef = useRef(null);
  const [errorText, setErrorText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const searchSuggestions = useSearchSuggestions(searchQuery);
  const gaContext = useContext(AnalyticsContext);

  // Close on page switch
  useEffect(() => {
    setSearchQuery("");
  }, [location]);

  useIsomorphicLayoutEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.focus();
    }
  });

  const onSubmit = (e: Event) => {
    e.preventDefault();

    if (inputRef.current !== null) {
      const { value } = inputRef.current;

      if (!value || value.length <= 1) {
        setErrorText(t("SEARCH.ERROR_MINIMUM_CHARACTERS", { characters: 2 }));
        return;
      }

      history.push(`/search/${value}`);
      gaContext.searchTerm(value);
    }
  };

  return (
    <form className={cn(className, styles.block)} onSubmit={onSubmit}>
      <div className={cn(styles.container, "awardit-SearchContainer")}>
        <button type="submit" className={styles.submit}>
          <SearchIcon className={styles.icon} />
        </button>

        <input
          ref={inputRef}
          value={searchQuery}
          className={styles.input}
          type="text"
          placeholder={t("SEARCH.PLACEHOLDER")}
          onChange={e => setSearchQuery(e.target.value)}
        />

        {typeof setMode === "function" &&
          <button
            type="button"
            className={styles.close}
            onClick={() => setMode(MODE.NORMAL)}
          >
            <CloseIcon className={styles.icon} />
          </button>
        }

        {errorText &&
          <span
            className={styles.error}
          >{errorText}
          </span>
        }
      </div>

      {searchQuery.length > 1 &&
        <section className={styles.suggestions}>
          <Link
            className={styles.suggestion}
            style={{ zIndex: 101 }}
            to={`/search/${searchQuery}`}
          >
            <div className={styles.suggestionBorder} />

            <div className={styles.suggestionWrapper}>
              <p className={styles.suggestionName}>
                {`"${searchQuery}"`}
              </p>
            </div>
          </Link>
          {searchSuggestions.length > 1 && searchSuggestions.map((x, i) => (
            <Link
              key={x.name}
              className={styles.suggestion}
              style={{ zIndex: 100 - i }}
              to={x.urlKey}
            >
              <div className={styles.suggestionBorder} />

              <div className={styles.suggestionWrapper}>
                <p className={styles.suggestionName}>
                  {x.name}
                </p>
                {x.type !== "product" &&
                  <p className={styles.suggestionType}>
                    {t(`SEARCH.TYPE.${x.type.toUpperCase()}`)}
                  </p>
                }
              </div>
            </Link>
          ))}
        </section>
      }
    </form>
  );
};

export default SearchMenu;
